import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
    WebFamAuditEntry,
    WebFamAuditFilter,
    WebFamAuditUser,
} from '../models/audit.model';

/**
 * Dziennik zmian w drzewie. Wpisy powstają w triggerach bazodanowych
 * (migracja 012_audit_log.sql), backend tylko je udostępnia - stąd brak metod
 * zapisujących. Dostęp mają użytkownicy z prawem edycji i wyżej.
 */
@Injectable()
export class AuditService {
    constructor(private httpClient: HttpClient) {}

    /** Historia zmian jednej osoby - razem ze zdarzeniami i powiązaniami rodzinnymi. */
    getPersonHistory(
        treeId: string,
        personId: string,
        limit = 100,
        offset = 0
    ): Observable<WebFamAuditEntry[]> {
        const params = new HttpParams().set('limit', limit).set('offset', offset);
        return this.httpClient
            .get<WebFamAuditEntry[]>(
                `${environment.backendUrl}/audit/person/tree/${treeId}/person/${personId}`,
                { params }
            )
            .pipe(map((entries) => this.normalize(entries)));
    }

    /** Dziennik całego drzewa z filtrami. */
    getTreeHistory(treeId: string, filter: WebFamAuditFilter = {}): Observable<WebFamAuditEntry[]> {
        let params = new HttpParams()
            .set('limit', filter.limit ?? 100)
            .set('offset', filter.offset ?? 0);

        if (filter.user) {
            params = params.set('user', filter.user);
        }
        if (filter.from) {
            params = params.set('from', filter.from);
        }
        if (filter.to) {
            params = params.set('to', filter.to);
        }

        return this.httpClient
            .get<WebFamAuditEntry[]>(`${environment.backendUrl}/audit/tree/${treeId}`, { params })
            .pipe(map((entries) => this.normalize(entries)));
    }

    /** Użytkownicy występujący w dzienniku - do filtra. */
    getTreeUsers(treeId: string): Observable<WebFamAuditUser[]> {
        return this.httpClient
            .get<WebFamAuditUser[]>(`${environment.backendUrl}/audit/tree/${treeId}/users`)
            .pipe(map((users) => (Array.isArray(users) ? users : [])));
    }

    /** Backend zwraca surowy JSON - upewniamy się, że komponenty dostają tablice. */
    private normalize(entries: WebFamAuditEntry[]): WebFamAuditEntry[] {
        if (!Array.isArray(entries)) {
            return [];
        }
        return entries.map((entry) =>
            Object.assign(new WebFamAuditEntry(), entry, {
                changes: Array.isArray(entry.changes) ? entry.changes : [],
            })
        );
    }
}
